// 🎨 «컬러컷» 색 토큰 — 브랜드색 하나에서 장면색 4개를 뽑고, 장면마다 무슨 색을 쓸지·색이 어떻게 쓸려 들어오는지를 정한다.
//   정본 지도 docs/01-plan/features/motion-style-pack.md «컬러컷» 절. 순수 함수만(난수·시계·DOM 0) — 같은 입력 = 같은 색.
//   ★불변식 셋(테스트가 잠근다 · tests/unit/color-cut-palette.test.ts)★
//     (a) 어떤 브랜드색이 와도 4색 전부 그 위 글자(흰/먹 중 대비 큰 쪽) 대비 ≥ 4.5 — 색면 그대로와 카드·강조 상자가 깔린 자리 모두 · 4색 중 3색 이상이 흰 글자
//     (b) 4색의 평균 밝기(0~255)가 폭 90 창 안 — 어느 두 색이 이웃해도 밝기 차 ≤ 90
//     (c) 쓸림(0.18초 · 30fps) 전 프레임에서 프레임 간 평균 밝기 차 ≤ 25.5 — 색 쓸림이 번쩍임이 되지 않는다
import { BRIEF_INK, briefContrast, briefTextOn } from './motion-brief-shape';

/** 브랜드색이 없거나 무채색일 때의 기준색(부품 상자 목업 파랑). */
export const COLOR_CUT_DEFAULT_BRAND = '#2F5BFF';
/** 장면색 개수 — 장면 번호 % 4 로 돈다. */
export const COLOR_CUT_SLOTS = 4;
/**
 * 브랜드 색상각에서 돌리는 각도(도) — 브랜드 / 맞은편 따뜻한 쪽 / 왼쪽 이웃 둘. 10-02 견본 비교로 고름:
 *   +150° 는 짙게 하면 녹슨 갈색이 됐고(+135° 는 빨강에 앉는다) · −30° 는 브랜드와 너무 닮고 · +180° 는 올리브가 됐다 · +60° 는 자홍·보라(부모 판정 «눈이 아프고 값싸 보인다»).
 */
export const COLOR_CUT_HUE_STEPS: readonly number[] = Object.freeze([0, 135, -60, -100]);
/** 돌린 색이 서로 이만큼(도)은 떨어져야 한다 — 가까우면 45° 씩 더 돌린다. */
export const COLOR_CUT_HUE_MIN_GAP = 35;
/** 돌린 색의 채도 — 0.88 은 형광으로 보였다(10-02 부모 판정) → 0.72. */
export const COLOR_CUT_SATURATION = 0.72;
/** 4색 평균 밝기 창의 반폭(0~255) — 창 폭 90 = 불변식 (b). */
export const COLOR_CUT_LUMA_HALF = 45;
/** 창 가운데 밝기의 범위 — «짙은 쪽»(흰 글자가 서는 밝기)으로 모은다. */
export const COLOR_CUT_LUMA_CENTER: readonly [number, number] = Object.freeze([80, 110]) as readonly [number, number];
/** 글자 대비 하한 — 색면 위, 그리고 카드·강조 상자가 깔린 자리 위에서도. */
export const COLOR_CUT_MIN_CONTRAST = 4.5;
/** 글자색이 섞여 깔리는 카드 바탕 — 어두운 팔레트 카드(흰 10%). 이 위 글자 대비도 ≥ 4.5. */
export const COLOR_CUT_TEXT_TINT = 0.1;
/** 강조 상자(강조 낱말 뒤 글자색 18%) — 그 위 낱말 대비 하한 3(강조 낱말은 굵고 큰 글자 · 4.5 까지 올리면 장면색이 전부 탁해진다 — 10-02 견본 A). */
export const COLOR_CUT_BOX_TINT = 0.18;
export const COLOR_CUT_BOX_MIN_CONTRAST = 3;
/** 밝은 장면(먹 글자) 카드 바탕 — 검정 4%(여유 포함 6%). */
export const COLOR_CUT_BLACK_TINT = 0.06;
/** 자막 띠(먹)와 장면색의 대비 하한 — 띠가 색면에 묻히지 않는다. */
export const COLOR_CUT_BAND_CONTRAST = 1.8;
/** 장표 → 장표 쓸림 길이(초) — 원 설계서 3-1 ColorCut. */
export const COLOR_CUT_WIPE_SEC = 0.18;
/** 영상·그림 장면 ↔ 장표 쓸림 길이(초) — 상대 밝기를 모르므로 전환 길이로 늘린다(프레임당 ≤ 255×1.25/24 ≈ 13.3). */
export const COLOR_CUT_WIPE_LONG_SEC = 0.8;
/** 자막 띠 — 먹 띠 + 흰 글자(대비 17:1). 밝은 색면(노랑 등) 위에서 흰 띠는 경계가 흐려져 먹으로 고정한다. */
export const COLOR_CUT_CAPTION = Object.freeze({ bg: 'rgba(17,24,39,0.82)', text: '#FFFFFF' });

const HEX6 = /^#[0-9a-fA-F]{6}$/;
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const rgbOf = (hex: string): [number, number, number] => [0, 2, 4].map((i) => parseInt(hex.slice(1 + i, 3 + i), 16)) as [number, number, number];
const hexOf = (r: number, g: number, b: number): string => `#${[r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** 평균 밝기(0~255 · Rec.601) — 급변 검사(`analyze.py` 전체 화면 평균 밝기)와 같은 자. */
export const colorCutLuma = (hex: string): number => {
  const [r, g, b] = rgbOf(HEX6.test(hex) ? hex : '#FFFFFF');
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

const hslOf = (hex: string): { h: number; s: number; l: number } => {
  const [r, g, b] = rgbOf(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s, l };
};
const hslHex = (h: number, s: number, l: number): string => {
  const hh = (((h % 360) + 360) % 360) / 60;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const [r, g, b] = hh < 1 ? [c, x, 0] : hh < 2 ? [x, c, 0] : hh < 3 ? [0, c, x] : hh < 4 ? [0, x, c] : hh < 5 ? [x, 0, c] : [c, 0, x];
  const m = l - c / 2;
  return hexOf((r + m) * 255, (g + m) * 255, (b + m) * 255);
};

/** 두 색을 섞는다(`p` = 위에 얹는 색의 불투명도). */
export const colorCutMix = (under: string, over: string, p: number): string => {
  const [ur, ug, ub] = rgbOf(under), [or, og, ob] = rgbOf(over);
  return hexOf(ur + (or - ur) * p, ug + (og - ug) * p, ub + (ob - ub) * p);
};

/**
 * 이 장면색에서 본문 글자가 놓이는 바탕 전부 — 색면 그대로 · 글자색이 10% 섞인 자리(흰 틴트 카드) · (먹 글자면) 검정 틴트 카드.
 *   무대가 내려주는 색 토큰(`colorCutContentColors`)으로 `layouts.tsx` 의 카드 7종·강조 3종이 만드는 바탕이 전부 이 안에 든다(테스트가 대조).
 */
export function colorCutSurfaces(color: string): string[] {
  const text = briefTextOn(color);
  return [color, colorCutMix(color, text, COLOR_CUT_TEXT_TINT), ...(text === BRIEF_INK ? [colorCutMix(color, '#000000', COLOR_CUT_BLACK_TINT)] : [])];
}

/** 이 색 위 글자 대비 — 글자(흰/먹 중 대비 큰 쪽)와 `colorCutSurfaces` 가운데 가장 나쁜 바탕의 대비. */
export const colorCutTextContrast = (bg: string): number => {
  const text = briefTextOn(bg);
  return Math.min(...colorCutSurfaces(bg).map((s) => briefContrast(text, s)));
};

/** 브랜드색으로 쓸 수 있는가 — #rrggbb 이고 채도가 있다(흰·검정·회색은 색상각이 없다). */
export const colorCutIsChromatic = (hex: string | null | undefined): hex is string => {
  if (!hex || !HEX6.test(hex)) return false;
  const { s, l } = hslOf(hex);
  return s >= 0.2 && l > 0.06 && l < 0.97;
};

/** 강조 상자 위 낱말 대비 — 글자색 18% 가 깔린 자리와 글자. */
export const colorCutBoxContrast = (bg: string): number => {
  const text = briefTextOn(bg);
  return briefContrast(text, colorCutMix(bg, text, COLOR_CUT_BOX_TINT));
};

/** 장면색으로 쓸 수 있는가 — 글자 대비(색면·카드) ≥ 4.5 · 강조 상자 위 ≥ 3 · 먹 자막 띠와 갈린다 · (`white`) 흰 글자가 서는 짙은 색. */
const usable = (hex: string, white: boolean): boolean =>
  colorCutTextContrast(hex) >= COLOR_CUT_MIN_CONTRAST && colorCutBoxContrast(hex) >= COLOR_CUT_BOX_MIN_CONTRAST
  && briefContrast(hex, BRIEF_INK) >= COLOR_CUT_BAND_CONTRAST && (!white || briefTextOn(hex) === '#FFFFFF');

/** 짙게 하면 탁해지는 색상각을 가까운 끝으로 밀어낸다 — 14~80°(짙은 주황·노랑 = 갈색·올리브) · 255~335°(보라·자홍). */
const snapHue = (deg: number): number => {
  const x = ((deg % 360) + 360) % 360;
  if (x > 14 && x < 80) return x < 47 ? 8 : 88;
  if (x > 255 && x < 335) return x < 295 ? 245 : 345;
  return x;
};
const hueGap = (a: number, b: number): number => { const d = Math.abs(a - b) % 360; return Math.min(d, 360 - d); };

/** 장면색 4개의 색상각 — 첫 칸은 브랜드 그대로, 나머지는 돌리고·밀어내고·서로 35° 이상 벌린다. */
export function colorCutHues(brandHue: number): number[] {
  const out = [((brandHue % 360) + 360) % 360];
  for (const step of COLOR_CUT_HUE_STEPS.slice(1)) {
    let raw = brandHue + step, x = snapHue(raw);
    // 밀어낸 자리가 이미 쓴 색과 겹치면 원래 각을 45° 씩 더 돌려 다시 밀어낸다(밀어낸 값을 돌리면 같은 끝에 다시 앉는다).
    for (let i = 0; i < 8 && out.some((o) => hueGap(x, o) < COLOR_CUT_HUE_MIN_GAP); i++) { raw -= 45; x = snapHue(raw); }
    out.push(x);
  }
  return out;
}

/** 한 색상각에서 창·대비를 만족하는 색 — `preferL` 에 가장 가까운 명도. 채도를 조금씩 낮춰 가며 찾는다. */
function pickInWindow(h: number, sat: number, preferL: number, lo: number, hi: number, white: boolean): string | null {
  for (const s of [sat, Math.min(sat, 0.6), 0.45, 0.3]) {
    let best: string | null = null, bestD = Infinity;
    for (let i = 8; i <= 92; i++) {
      const l = i / 100;
      const hex = hslHex(h, s, l);
      const y = colorCutLuma(hex);
      if (y < lo || y > hi || !usable(hex, white)) continue;
      const d = Math.abs(l - preferL);
      if (d < bestD) { best = hex; bestD = d; }
    }
    if (best) return best;
  }
  return null;
}

/**
 * 브랜드색 → 장면색 4개. ① 브랜드가 창·대비를 이미 만족하면 **그대로** 첫 색 ② 아니면 색상각·채도는 두고 명도만 옮김(가까운 쪽)
 * ③ 나머지 셋은 색상각을 돌린 **짙은 색(흰 글자)** — 네 장 중 흰 글자 장면이 3장 이상이라 통일감이 선다. 무채색·형식 오류는 기본 파랑에서 뽑는다.
 */
export function colorCutPalette(brand: string | null | undefined): readonly string[] {
  const seed = colorCutIsChromatic(brand) ? brand.toUpperCase() : COLOR_CUT_DEFAULT_BRAND;
  const { h, s, l } = hslOf(seed);
  const c = clamp(colorCutLuma(seed), COLOR_CUT_LUMA_CENTER[0], COLOR_CUT_LUMA_CENTER[1]);
  const lo = c - COLOR_CUT_LUMA_HALF, hi = c + COLOR_CUT_LUMA_HALF;
  const y0 = colorCutLuma(seed);
  const keep = y0 >= lo && y0 <= hi && usable(seed, false);
  const safe = hslHex(h, 0.3, 0.3); // 끝까지 못 찾을 때(실제로는 타지 않는다 — 색상각 전수 테스트가 잠근다)
  const first = keep ? seed : pickInWindow(h, Math.max(s, 0.6), l, lo, hi, false) ?? safe;
  const rest = colorCutHues(h).slice(1).map((hue) => pickInWindow(hue, COLOR_CUT_SATURATION, 0.45, lo, hi, true) ?? safe);
  return Object.freeze([first, ...rest]);
}

/** 장면 번호가 없을 때의 차선 — 장표 종류별 고정 칸(같은 종류가 이어지면 색이 안 바뀌는 약점이 있다). */
const SLOT_OF_TYPE: Readonly<Record<string, number>> = Object.freeze({
  fullText: 0, kineticTypo: 0, bigNumber: 1, statRow: 1, checkList: 2, stepFlow: 3, cardGrid: 1, comparePair: 3, splitFocus: 2, mediaFocus: 0, posterStack: 0,
});
const slotOfType = (type: string | null | undefined): number => SLOT_OF_TYPE[type ?? ''] ?? 0;
const mod = (n: number, m: number): number => ((Math.trunc(n) % m) + m) % m;

export interface ColorCutScene {
  /** 이번 장면 색. */
  color: string;
  /** 앞 장면이 보여 준 색(쓸림이 덮는 바탕) — 앞 장면이 장표가 아니면 null. */
  from: string | null;
  /** `index` = 장면 번호로 골랐다(이웃이 항상 다른 색) · `type` = 번호가 없어 장표 종류로 골랐다(차선). */
  mode: 'index' | 'type';
}

/**
 * 이번 장면·앞 장면의 색. `sceneIndex`(전체 씬 번호 · 0부터)가 있으면 팔레트[번호 % 4] · 앞 = 팔레트[(번호−1) % 4].
 * 없으면 장표 종류별 고정 칸으로 물러난다(무대가 죽지 않는다 · `mode` 로 구분).
 */
export function colorCutScene(palette: readonly string[], scene: { sceneIndex?: number; type: string; prevType?: string | null }): ColorCutScene {
  const n = palette.length;
  if (typeof scene.sceneIndex === 'number' && Number.isFinite(scene.sceneIndex)) {
    return { color: palette[mod(scene.sceneIndex, n)], from: scene.prevType ? palette[mod(scene.sceneIndex - 1, n)] : null, mode: 'index' };
  }
  return { color: palette[mod(slotOfType(scene.type), n)], from: scene.prevType ? palette[mod(slotOfType(scene.prevType), n)] : null, mode: 'type' };
}

/** 쓸림 진행도(0~1) — 선형과 부드러운 계단의 반반(최대 기울기 1.25 · 시작·끝이 살짝 눌린다). 이징 토큰의 급한 진입은 프레임당 밝기 차를 2.5배로 키워 쓰지 않는다. */
export function colorCutWipe(tSec: number, durSec: number): number {
  if (!(durSec > 0)) return tSec >= 0 ? 1 : 0;
  const x = clamp(tSec / durSec, 0, 1);
  return 0.5 * x + 0.5 * (x * x * (3 - 2 * x));
}

/** 쓸림 중 화면 평균 밝기(색면만 · 0~255) — 아래에서 `p` 만큼 새 색. 불변식 (c) 를 재는 자. */
export const colorCutWipeLuma = (fromLuma: number, toLuma: number, p: number): number => fromLuma + (toLuma - fromLuma) * clamp(p, 0, 1);

/**
 * 색면 위 장표 색 토큰 — `layouts.tsx` 는 그대로 두고 토큰만으로 «색면·카드·강조 상자 위 글자 대비 ≥ 4.5» 를 지킨다:
 *   글자·보조 글자·강조색·상태색(✕·✓) = 대비 큰 글자색(흰/먹) 하나 — 원색 위 둘째 색은 대비를 못 지킨다(강조색이 장면색에 묻히는 사고).
 *   옅은 강조 바탕(`accentDim` — 강조색 카드·형광펜 강조)은 글자 **반대쪽**으로 깐다(흰 글자면 검정 틴트 · 먹 글자면 흰 틴트) → 바탕이 깔리면 대비가 오른다.
 *   카드 틴트는 `isDark` 가 고른다(흰 글자 = 흰 10% · 먹 글자 = 검정 4%) — 그 바탕 위 대비는 팔레트가 미리 본다(`colorCutSurfaces`).
 */
export function colorCutContentColors<C extends { bg?: string; textMain?: string; textSub?: string; accent: string; accentDim?: string; accentGlow?: string; isDark?: boolean; status?: unknown }>(base: C, color: string): C {
  const text = briefTextOn(color);
  const light = text === '#FFFFFF';
  const status = Object.fromEntries(['success', 'danger', 'warning', 'info', 'neutral'].flatMap((k) => [[k, text], [`${k}Text`, text], [`${k}Glow`, light ? 'rgba(255,255,255,0.30)' : 'rgba(17,24,39,0.20)']]));
  return {
    ...base, bg: color, textMain: text, textSub: text, accent: text,
    accentDim: light ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.35)', accentGlow: light ? 'rgba(255,255,255,0.55)' : 'rgba(17,24,39,0.45)', isDark: light, status,
  } as C;
}
